import { Loader } from '@10xLabs/react-web/components/Loader'
import { NetworkStatus, useQuery } from '@apollo/client'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import { PaginationList } from 'components/PaginationList'
import { Button } from 'components/v2/Button'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { useLogin } from 'features/Login/hooks'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { createRoutingProjectsCollection } from 'features/RoutingProjects/collections'
import { CreateRoutingProjectModal } from 'features/RoutingProjects/components/CreateProjectModal'
import { RoutingProjectsList } from 'features/RoutingProjects/components/RoutingProjectsList'
import { GET_ROUTING_PROJECTS } from 'features/RoutingProjects/queries'
import { canRead, canWrite } from 'features/Users/utils'
import { getQueries } from 'utils/route'
import { RoutingProjectFragment } from 'gql/generated/graphql'

const LIMIT = 30
const messages = defineMessages({
  helmet: {
    id: 'routingProjects.helmet',
    defaultMessage: 'Projects | Nexbus Admin'
  }
})

const RoutingProjectsView = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const [isOpenCreateProject, setIsOpenCreateProject] = useState(false)
  const queries = getQueries(location.search)
  const { page = '1' } = queries
  const pageNb = parseInt(page)

  const { data, loading, error, networkStatus, refetch } = useQuery(GET_ROUTING_PROJECTS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {},
      pagination: {
        page: pageNb,
        size: LIMIT
      }
    }
  })
  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [intl])
  const routingProjectsCollection = useMemo(() => {
    return createRoutingProjectsCollection((data?.routingProjects.items as RoutingProjectFragment[]) || [])
  }, [data])

  if (!canRead(FEATURES.ROUTING_PROJECT, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.ROUTING_PROJECT} />
  }

  const handleOpenCreateProject = (): void => {
    setIsOpenCreateProject(true)
  }

  let content: ReactElement

  if (loading || networkStatus === NetworkStatus.refetch) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="routingProjects.error"
            defaultMessage="An error occurred while fetching the projects. Please try again."
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            kind="primary"
            onClick={(): void => {
              refetch()
            }}
          >
            <FormattedMessage id="common.retry" defaultMessage="Retry" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (routingProjectsCollection.isEmpty) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="routingProjects.empty"
            defaultMessage="You don't have any projects yet. Create a new project to get started."
          />
        </EmptyState.Description>
        {canWrite(FEATURES.ROUTING_PROJECT, login.accessToken.policies) && (
          <EmptyState.Action>
            <Button kind="primary" onClick={handleOpenCreateProject}>
              <FormattedMessage id="routingProjects.createProject" defaultMessage="Create a new project" />
            </Button>
          </EmptyState.Action>
        )}
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <RoutingProjectsList routingProjectsCollection={routingProjectsCollection} />
        <PaginationList
          page={pageNb}
          limit={LIMIT}
          totalItems={data?.routingProjects.total || 0}
          currentItemsCount={data?.routingProjects.items.length || 0}
        />
      </>
    )
  }

  return (
    <Content>
      <ContentHeader isResponsive>
        <Heading.H3>
          <FormattedMessage id="routingProjects.header.label" defaultMessage="Projects" />
        </Heading.H3>
        {canWrite(FEATURES.ROUTING_PROJECT, login.accessToken.policies) && (
          <Button type="button" kind="primary" onClick={handleOpenCreateProject}>
            <FormattedMessage id="routingProjects.newProject" defaultMessage="Create new project" />
          </Button>
        )}
      </ContentHeader>
      <ContentBody>
        <section>{content}</section>
      </ContentBody>
      {isOpenCreateProject && (
        <CreateRoutingProjectModal
          closeModal={(): void => {
            setIsOpenCreateProject(false)
          }}
        />
      )}
    </Content>
  )
}

export default withLastUrl(RoutingProjectsView)
